import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import { getListingFullAddressString } from 'common/listingHelpers';
import { LISTING_STATUS_LIST } from 'common/listingConstants';
import { USER_ROLE_ADMIN } from 'common/userRoles';
import {
  SR_API_ACTION_GET_APARTMENT_COMPLEX_UNIT_GROUP_LIST,
  SR_API_ACTION_CREATE_OR_EDIT_APARTMENT_COMPLEX_UNIT_GROUP,
  SR_API_ACTION_DELETE_APARTMENT_COMPLEX_UNIT_GROUP,
  SR_API_ACTION_GET_LISTING_LIST,
} from 'api/actionTypes';

import SrDarkModal from 'client/components/SrDarkModal';
import Navbar from 'client/components/Navbar';
import H1 from 'client/components/H1';
import SrFormikField from 'client/components/SrFormikField';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import branch from 'client/core/branch';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedRoles from 'client/helpers/allowedRoles';
import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';
import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';
import postWithState from 'client/actions/postWithState';
import { SCREEN_ID_ADMIN_APARTMENT_COMPLEX_UNIT_GROUPS } from 'client/constants/screenIds';

const searchListingsFormField = [
  {
    id: 'addressStreetName',
    title: 'Street Name',
    type: 'short_text',
    properties: {
      placeholder: 'Sam Bass',
    },
    validations: ['required'],
  },
];

const formFields = [
  {
    id: 'name',
    title: 'Name',
    type: 'short_text',
    properties: {
      placeholder: 'Sam Bass',
    },
    validations: ['required'],
  },
  {
    id: 'listingIds',
    title: 'Listing ids',
    type: 'long_text',
    properties: {
      placeholder: '[1,2,3]',
    },
    validations: ['required', 'stringifiedArray'],
  },
];

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScrollToTop
@withScreenId(SCREEN_ID_ADMIN_APARTMENT_COMPLEX_UNIT_GROUPS)
@branch()
class AdminApartmentComplexUnitGroups extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin Apartment Complex Unit Groups | Sunroom Rentals',
    section: 'Admin',
  });

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      showModal: false,
      apartmentComplexUnitGroups: [],
      defaultFormValues: {},
      searchResult: [],
    };
  }

  componentDidMount() {
    this._getApartmentComplexUnitsGroupList();
  }

  _getApartmentComplexUnitsGroupList = () => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_GET_APARTMENT_COMPLEX_UNIT_GROUP_LIST,
      onSuccess: (apartmentComplexUnitGroups) => {
        this.setState({ apartmentComplexUnitGroups });
      },
    });
  };

  _onSubmitForm = (values) => {
    const { defaultFormValues } = this.state;
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_CREATE_OR_EDIT_APARTMENT_COMPLEX_UNIT_GROUP,
      payload: {
        apartmentComplexUnitGroupId: defaultFormValues.apartmentComplexUnitGroupId,
        name: values.name,
        listingIds: JSON.parse(values.listingIds),
      },
      onSuccess: () => {
        this._getApartmentComplexUnitsGroupList();
        this._onCloseModal();
      },
    });
  };

  _onEditGroup = (e) => {
    const { apartmentComplexUnitGroups } = this.state;
    const apartmentComplexUnitGroupId = parseInt(e.target.dataset.id, 10);

    const apartmentComplexUnitGroup = apartmentComplexUnitGroups.find(
      ({ id }) => id === apartmentComplexUnitGroupId,
    );

    this.setState({
      showModal: true,
      defaultFormValues: {
        apartmentComplexUnitGroupId: apartmentComplexUnitGroup.id,
        name: apartmentComplexUnitGroup.name,
        listingIds: JSON.stringify(apartmentComplexUnitGroup.listings.map((listing) => listing.id)),
      },
    });
  };

  _addNewGroup = () => {
    this.setState({
      showModal: true,
      defaultFormValues: {},
    });
  };

  _onDeleteGroup = (e) => {
    const { dispatch } = this.props;
    const { apartmentComplexUnitGroups } = this.state;
    const apartmentComplexUnitGroupId = parseInt(e.target.dataset.id, 10);

    const apartmentComplexUnitGroup = apartmentComplexUnitGroups.find(
      ({ id }) => id === apartmentComplexUnitGroupId,
    );

    if (global.confirm(`Are you sure you want to delete "${apartmentComplexUnitGroup.name}"?`)) {
      dispatch(postWithState, {
        action: SR_API_ACTION_DELETE_APARTMENT_COMPLEX_UNIT_GROUP,
        payload: {
          apartmentComplexUnitGroupId,
        },
        onSuccess: this._getApartmentComplexUnitsGroupList,
      });
    }
  };

  _onCloseModal = () => {
    this.setState({
      showModal: false,
      searchResult: [],
      defaultFormValues: {},
    });
  };

  _onSearch = ({ addressStreetName }) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_GET_LISTING_LIST,
      payload: {
        filters: {
          addressStreetName,
          status: LISTING_STATUS_LIST,
        },
        sort: [['addressStreetDisplay', 'asc']],
      },
      queryId: 'list',
      onSuccess: ({ data }) => {
        this.setState({ searchResult: data });
      },
    });
  };

  render() {
    const { apartmentComplexUnitGroups, defaultFormValues, showModal, searchResult } = this.state;

    return (
      <div className="page-admin">
        <div className="adminContainer">
          <Navbar redirectLogoTo="/admin" />

          <div className="contentContainer">
            <H1>Apartment Complex Groups</H1>

            <button className="floatButton add" type="button" onClick={this._addNewGroup} />

            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Listings</th>
                  <th>Options</th>
                </tr>
              </thead>
              <tbody>
                {apartmentComplexUnitGroups.map(({ id, name, listings }) => (
                  <tr key={id}>
                    <td>{name}</td>
                    <td>{listings.map((listing) => listing.id).join(',')}</td>
                    <td>
                      <div data-id={id} onClick={this._onEditGroup}>
                        Edit
                      </div>
                      <div data-id={id} onClick={this._onDeleteGroup}>
                        Delete
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {showModal && (
              <SrDarkModal onClose={this._onCloseModal}>
                <div onClick={preventDefaultPropagation} className="adminDefaultModal">
                  <Formik
                    enableReinitialize
                    initialValues={createInitialValues({
                      formFields: searchListingsFormField,
                      defaultValues: {},
                    })}
                    onSubmit={this._onSearch}
                    validate={createFormValidationFn(searchListingsFormField)}
                    render={(formikBag) => {
                      return (
                        <div>
                          {searchListingsFormField.map((formField) => (
                            <SrFormikField
                              key={formField.id}
                              formField={formField}
                              formikBag={formikBag}
                            />
                          ))}

                          <ButtonDeprecated
                            onClick={formikBag.handleSubmit}
                            disabled={!formikBag.isValid}
                          >
                            search
                          </ButtonDeprecated>
                          {searchResult.length > 0 && (
                            <table>
                              <thead>
                                <tr>
                                  <th>Listing ID</th>
                                  <th>Address</th>
                                </tr>
                              </thead>
                              <tbody>
                                {searchResult.map((listing) => (
                                  <tr key={listing.id}>
                                    <td>{listing.id}</td>
                                    <td>{getListingFullAddressString(listing)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Formik
                    enableReinitialize
                    initialValues={createInitialValues({
                      formFields: formFields,
                      defaultValues: defaultFormValues,
                    })}
                    onSubmit={this._onSubmitForm}
                    validate={createFormValidationFn(formFields)}
                    render={(formikBag) => {
                      return (
                        <div>
                          {formFields.map((formField) => (
                            <SrFormikField
                              key={formField.id}
                              formField={formField}
                              formikBag={formikBag}
                            />
                          ))}

                          <ButtonDeprecated
                            className="save"
                            onClick={formikBag.handleSubmit}
                            disabled={!formikBag.isValid}
                          >
                            {defaultFormValues.apartmentComplexUnitGroupId ? 'update' : 'create'}
                          </ButtonDeprecated>
                        </div>
                      );
                    }}
                  />
                </div>
              </SrDarkModal>
            )}
          </div>
        </div>
      </div>
    );
  }
}

AdminApartmentComplexUnitGroups.diplayName = 'AdminApartmentComplexUnitGroups';

export default AdminApartmentComplexUnitGroups;
